/**
 * EmployeeAddDeductTaxTables — thin rendering shell.
 *
 * All state, data fetching, and business logic live in:
 *   src/Components/AddDeductTaxMapping/useAddDeductTaxTable.js
 *
 * Sub-components:
 *   AddDedRow            — a single memoized additions/deductions row
 *   TaxRow               — a single memoized EE/ER taxes row
 *   AddDedTableHead      — Additions/Deductions column headers + add button
 *   TaxTableHead         — EE & ER Taxes column headers + add button
 *   AddDedTableFooter    — totals for additions & deductions
 *   TaxTableFooter       — totals for EE & ER taxes
 */
import React, { useMemo, useCallback } from "react";
import { Box, Table, TableBody, TableContainer } from "@mui/material";
import { FieldArray } from "formik";
import PayrollsPopup from "./PayrollPopup";
import AddCOA from "../QuickAdd/AddCOA";

import {
    useAddDeductTaxTable,
    AddDedRow,
    TaxRow,
    AddDedTableHead,
    TaxTableHead,
    AddDedTableFooter,
    TaxTableFooter,
} from "./AddDeductTaxMapping";

const EmployeeAddDeductTaxTables = ({
    row,
    addDedItems,
    taxItems,
    formValues,
    accountOptions,
    corporationId,
    onAddNewMappedItem,
    refreshTrigger,
    onTotalsChange,
    isReadOnly,
}) => {
    const {
        values,
        setFieldValue,
        errors,
        touched,
        enqueueSnackbar,
        removingIndex,
        setRemovingIndex,
        removingTaxIndex,
        setRemovingTaxIndex,
        alertPopup,
        showDeleteConfirm,
        hidePopup,
        openAddCOA,
        setOpenAddCOA,
        pendingCOAField,
        setPendingCOAField,
        showItemColumn,
        addAndDeductions,
        eeAndErTaxes,
        mappedToOptionsMap,
        mappedToLookup,
        enhancedAccountOptions,
        accountOptionsMap,
        fetchCoaDetails,
        refreshAccounts,
        formatCurrency,
    } = useAddDeductTaxTable({
        row, formValues, corporationId, accountOptions,
        addDedItems, taxItems,
        onTotalsChange, refreshTrigger, onAddNewMappedItem, isReadOnly,
    });

    // ─── Grouped contexts (passed as single 'ctx' prop) ───────────────────
    const addDedCtx = useMemo(() => ({
        isTax: false,
        showItemColumn,
        isReadOnly,
        formValuesDepartment: formValues?.department,
        formValuesJob: formValues?.job,
        mappedToOptionsMap,
        mappedToLookup,
        accountOptionsMap,
        accountOptions: enhancedAccountOptions,
    }), [
        showItemColumn,
        isReadOnly,
        formValues?.department,
        formValues?.job,
        mappedToOptionsMap,
        mappedToLookup,
        accountOptionsMap,
        enhancedAccountOptions,
    ]);

    const taxCtx = useMemo(() => ({
        ...addDedCtx,
        isTax: true,
    }), [addDedCtx]);

    // ─── Grouped handlers builders (passed as single 'handlers' prop) ─────
    const getAddDedHandlers = useCallback((remove) => ({
        setFieldValue,
        enqueueSnackbar,
        fetchCoaDetails,
        onAddNewMappedItem,
        remove,
        setRemovingIndex,
        showDeleteConfirm,
        setOpenAddCOA,
        setPendingCOAField,
    }), [
        setFieldValue,
        enqueueSnackbar,
        fetchCoaDetails,
        onAddNewMappedItem,
        setRemovingIndex,
        showDeleteConfirm,
        setOpenAddCOA,
        setPendingCOAField,
    ]);

    const getTaxHandlers = useCallback((remove) => ({
        setFieldValue,
        enqueueSnackbar,
        fetchCoaDetails,
        onAddNewMappedItem,
        remove,
        setRemovingIndex: setRemovingTaxIndex,
        showDeleteConfirm,
        setOpenAddCOA,
        setPendingCOAField,
    }), [
        setFieldValue,
        enqueueSnackbar,
        fetchCoaDetails,
        onAddNewMappedItem,
        setRemovingTaxIndex,
        showDeleteConfirm,
        setOpenAddCOA,
        setPendingCOAField,
    ]);

    return (
        <>
            {/* ─── Additions & Deductions Table ─────────────────────────── */}
            <FieldArray name="addAndDeductions">
                {({ push, remove }) => (
                    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                        <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 2, overflowX: "auto" }}>
                            <Table size="small" sx={{ minWidth: 750, "& .MuiTableCell-root": { borderBottom: "1px solid #f2f2f2" } }}>
                                <AddDedTableHead
                                    showItemColumn={showItemColumn}
                                    isReadOnly={isReadOnly}
                                    onAddRow={() =>
                                        push({
                                            id: Date.now(),
                                            addDeduction: "",
                                            item: "",
                                            mappedTo: "",
                                            coa: "",
                                            coaName: "",
                                            amount: "0.00",
                                        })
                                    }
                                />
                                <TableBody>
                                    {addAndDeductions.map((addDedRow, index) => (
                                        <AddDedRow
                                            key={addDedRow.id || index}
                                            rowData={{
                                                row: addDedRow,
                                                index,
                                                isRemoving: removingIndex === index,
                                                touchedRow: touched?.addAndDeductions?.[index],
                                                errorRow: errors?.addAndDeductions?.[index],
                                            }}
                                            ctx={addDedCtx}
                                            handlers={getAddDedHandlers(remove)}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <AddDedTableFooter addAndDeductions={addAndDeductions} formatCurrency={formatCurrency} />
                    </Box>
                )}
            </FieldArray>

            {/* ─── EE & ER Taxes Table ──────────────────────────────────── */}
            <FieldArray name="eeAndErTaxes">
                {({ push, remove }) => (
                    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                        <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 2, overflowX: "auto" }}>
                            <Table size="small" sx={{ minWidth: 750, "& .MuiTableCell-root": { borderBottom: "1px solid #f2f2f2" } }}>
                                <TaxTableHead
                                    showItemColumn={showItemColumn}
                                    isReadOnly={isReadOnly}
                                    onAddRow={() =>
                                        push({
                                            id: Date.now(),
                                            taxType: "",
                                            item: "",
                                            mappedTo: "",
                                            coa: "",
                                            coaName: "",
                                            amount: "0.00",
                                        })
                                    }
                                />
                                <TableBody>
                                    {eeAndErTaxes.map((taxRow, index) => (
                                        <TaxRow
                                            key={taxRow.id || index}
                                            rowData={{
                                                row: taxRow,
                                                index,
                                                isRemoving: removingTaxIndex === index,
                                                touchedRow: touched?.eeAndErTaxes?.[index],
                                                errorRow: errors?.eeAndErTaxes?.[index],
                                            }}
                                            ctx={taxCtx}
                                            handlers={getTaxHandlers(remove)}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TaxTableFooter eeAndErTaxes={eeAndErTaxes} formatCurrency={formatCurrency} />
                    </Box>
                )}
            </FieldArray>


            <PayrollsPopup
                open={alertPopup.open}
                popupType={alertPopup.type}
                message={alertPopup.message}
                onConfirm={alertPopup.onConfirm}
                onCancel={hidePopup}
            />

            <AddCOA
                open={openAddCOA}
                corporationId={corporationId || values?.corporationId || values?.corpId || ""}
                corporationName={values?.corporation?.corpName || values?.corporation || ""}
                onClose={() => {
                    setOpenAddCOA(false);
                    setPendingCOAField("");
                }}
                setFieldValue={setFieldValue}
                pendingCOAField={pendingCOAField}
                onSuccess={async () => {
                    await refreshAccounts();
                }}
            />
        </>
    );
};

export default EmployeeAddDeductTaxTables;
